/**
 * Real `createGi` — signs the manifest client-side (same Ed25519 +
 * SHA-256 path the mock uses, AD-confirmed byte-compatible), then
 * POSTs the signed record to the engine.
 *
 * The UI still owns the browser-side signing step. That is not an
 * accident: the real engine (`gi-engine::store_node`) re-verifies
 * the manifest signature against the author's public key, which
 * means the client MUST sign with a keypair whose private half
 * the server never sees. Running the signing server-side would
 * break that guarantee.
 *
 * Returned shape matches the mock exactly so `Step7Sign.tsx`
 * (download button, back-to-vault, etc.) does not care which mode
 * produced the bundle.
 */

import { bytesToHex } from '@noble/hashes/utils';

import type {
  ActivityEvent,
  ApiCreateGiResponse,
  Ed25519Signature,
  EntityId,
  GiBundle,
  GiStatus,
  GiSummary,
  Hash256,
  Lineage,
  LineageEntry,
  Manifest,
  SignedEntity,
  SparkDraft,
  SparkOutput,
  TrustedTimestamp,
} from '../types';
import { apiFetch } from '../http';
import {
  canonicalHash,
  canonicalJsonStringify,
  generateKeyPair,
  hashBytes,
  randomHex,
  signDigest,
} from '../mock/sign';
import { appendActivity, nowTimestamp, upsertRecord, type GiRecord } from '../mock/store';

export async function createGi(
  draft: SparkDraft,
  wasm_bytes?: Uint8Array,
): Promise<SparkOutput> {
  const kp = generateKeyPair();

  const root_id = randomUuid();
  const created_at: TrustedTimestamp = nowTimestamp();

  const wasm_hash: Hash256 =
    draft.wasm_hash ??
    (wasm_bytes ? hashBytes(wasm_bytes) : `sha256:${randomHex(32)}`);
  const source_hash: Hash256 = draft.source_hash ?? `sha256:${randomHex(32)}`;

  const contract = {
    inputs: draft.inputs,
    outputs: draft.outputs,
    parameters: draft.parameters,
    behavior_rules: [],
    domain_adapter: draft.domain,
    macros: [],
  };
  const contract_hash = canonicalHash(contract);

  const manifest: Manifest = {
    gi_version: '0.1.0',
    root_id,
    version: draft.version,
    name: draft.name,
    description: draft.description,
    domain: draft.domain,
    author: { name: draft.author_name, public_key: kp.public_key },
    contract_hash,
    source_hash,
    wasm_hash,
    compiler: { name: 'browser-client', version: '0.1.0', target: 'wasm32-unknown' },
    lineage: { root_id, depth: 0 },
    created_at,
  };

  const manifest_hash = canonicalHash(manifest);
  const signature: Ed25519Signature = signDigest(
    hexToBytes(manifest_hash.slice('sha256:'.length)),
    kp,
  );

  const signed_entity: SignedEntity = {
    root_id,
    version: manifest.version,
    manifest_hash,
    signature,
    signer_public_key: kp.public_key,
    created_at,
  };

  // POST the signed record. Response carries the root_id + storage
  // sequence; we need sequence to upsert into the vault.
  const giResp = await apiFetch<ApiCreateGiResponse>('/gi', {
    method: 'POST',
    body: { manifest, signed_entity, contract },
  });

  // Build a single-entry lineage locally so the `.gi` bundle the UI
  // downloads carries a full shape. The engine persists just the
  // manifest+signed_entity+contract tuple (lineage is derived on
  // read in real mode — see real/gi.ts).
  const lineage: Lineage = {
    root_id,
    entries: [
      {
        index: 0,
        root_id,
        version: manifest.version,
        action: 'Genesis',
        parent_hash: null,
        entry_hash: canonicalHash({ root_id, version: manifest.version, parent: null }),
        signer: kp.public_key,
        signed_at: created_at,
      } satisfies LineageEntry,
    ],
  };

  const bundle: GiBundle = {
    bundle: 'gi/v1',
    manifest,
    signed_entity,
    contract,
    lineage,
  };
  const bundle_bytes = new TextEncoder().encode(canonicalJsonStringify(bundle));

  // Upsert into the server vault so the real vault snapshot
  // (GET /api/v1/vault, JWT-scoped) includes this GI immediately.
  // Best-effort — if the upsert fails we log but still return the
  // bundle so the user gets their download even on transient error.
  try {
    await apiFetch('/vault', {
      method: 'POST',
      body: {
        root_id,
        version: manifest.version,
        name: manifest.name,
        description: manifest.description,
        domain: typeof manifest.domain === 'string'
          ? manifest.domain
          : `Custom("${(manifest.domain as { Custom: string }).Custom}")`,
        author_name: manifest.author.name,
        created_at: created_at.iso,
        sequence: giResp.sequence,
      },
    });
  } catch {
    // Non-fatal: vault upsert failure should not block the Spark result.
  }

  // Mirror the new record into the mock store for backwards-compat
  // (getVaultSnapshot in mock mode + the Vault snapshot's localStorage
  // path both read from here). The server vault above is authoritative
  // in real mode; the mock store is authoritative in mock mode.
  const record: GiRecord = {
    manifest,
    signed_entity,
    contract,
    lineage,
    attestations: [],
    statuses: ['signed'] satisfies GiStatus[] as GiStatus[],
    counters: { signatures: 1, verifications: 0, executions: 0, forks: 0 },
    secret_key_hex: bytesToHex(kp.secret_key),
  };
  upsertRecord(record);
  const ev: ActivityEvent = {
    kind: 'sparked',
    root_id,
    name: manifest.name,
    at: created_at,
  };
  appendActivity(ev);

  const summary: GiSummary = {
    root_id,
    version: manifest.version,
    name: manifest.name,
    description: manifest.description,
    domain: manifest.domain,
    author_name: manifest.author.name,
    created_at,
    status: record.statuses,
    stats: record.counters,
  };

  return { bundle, summary, bundle_bytes };
}

function randomUuid(): EntityId {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  const hex = randomHex(16);
  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    `4${hex.slice(13, 16)}`,
    `8${hex.slice(17, 20)}`,
    hex.slice(20, 32),
  ].join('-');
}

function hexToBytes(hex: string): Uint8Array {
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i++) {
    out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  }
  return out;
}
